import { Parse } from "parse";
import moment from "moment";
import * as _ from "lodash";
import { getArraysBetweenTwoDates } from "./getDateInfo";

export const getPkgList = (user, filterOption) => {
  const pkgQuery = new Parse.Query(Parse.Object.extend("EmoticonPackage"));

  if (filterOption === "packages-in-progress") {
    pkgQuery.containedIn("status", ["creating", "editing"]);
  }

  if (filterOption === "packages-of-reviewed") {
    pkgQuery.containedIn("status", ["under review", "approved", "unapproved", "editing", "pre deleted"]);
  }

  if (filterOption === "my-package") {
    pkgQuery.containedIn("status", ["under review", "approved", "unapproved", "discontinue", "pre deleted"]);
  }

  return new Promise((resolve, reject) =>
    pkgQuery
      .equalTo("author", user)
      .descending("createdAt")
      .limit(100000000)
      .find()
      .then((pkgs) => resolve(pkgs))
      .catch((err) => {
        reject(err);
      }),
  );
};

/**
 * @description 유저가 만든 패키지(creating, discontinue, deleted를 제외한)와 해당 패키지에 매칭된 검수데이터를 가져오는 함수.
 * @param {ParseObject} user 유저 객체
 * @return {Array} 검색한 packageInspectionData 객체 배열
 */
export const getPkgsAndInspectData = (user) => {
  const standardDate = moment()
    .subtract(15, "days")
    .toDate();

  const pkgQuery = new Parse.Query(Parse.Object.extend("EmoticonPackage"))
    .equalTo("author", user)
    .descending("createdAt")
    .limit(1000000);

  const pkgInspectDateQuery = new Parse.Query(Parse.Object.extend("PackageInspectionData")).matchesQuery("package", pkgQuery.equalTo("status", "approved")).greaterThan("approvedAt", standardDate);

  const pkgInspectQuery = new Parse.Query(Parse.Object.extend("PackageInspectionData")).matchesQuery(
    "package",
    pkgQuery.containedIn("status", ["under review", "unapproved", "editing", "pre deleted"]),
  );

  return Parse.Query.or(pkgInspectDateQuery, pkgInspectQuery)
    .include("package")
    .limit(1000000)
    .find();
};

export const getPkgInfoById = (pkgId) => {
  return new Promise((resolve, reject) =>
    new Parse.Query(Parse.Object.extend("EmoticonPackage"))
      .get(pkgId)
      .then((pkg) =>
        resolve({
          name: pkg.get("name"),
          description: pkg.get("description"),
          status: pkg.get("status"),
          thumbnail: pkg.get("thumbnail"),
          textLang: pkg.get("textLang"),
          packType: pkg.get("packType"),
          movingStatus: pkg.get("movingStatus"),
          keyTexts: pkg.get("keyTexts") || [],
          createdAt: pkg.get("createdAt"),
        }),
      )
      .catch((err) => {
        console.log(err);
        if (err.code === 101 && err.message === "Object not found.") {
          reject("Package does not exist.");
        } else {
          reject("Failed to load package infomation.");
        }
      }),
  );
};

export const getPkgUnapprovedCauseData = (pkg) => {
  return new Promise((resolve, reject) => {
    new Parse.Query(Parse.Object.extend("PackageInspectionData"))
      .equalTo("package", pkg)
      .first()
      .then((inspectData) =>
        inspectData
          .get("unapprovedCause")
          .query()
          .ascending("createdAt")
          .find()
          .then((causes) => {
            let types = {
              isUnapprovedCauseThumbnail: false,
              isUnapprovedCauseName: false,
              isUnapprovedCauseDescription: false,
              isUnapprovedCausePackDeleting: false,
            };
            let explain = [];
            causes.forEach((cause) => {
              const actionType = cause.get("type").find((type) => type.itemType === "PACKAGE").actionType;

              explain.push(cause.get("explain"));

              if (actionType === "EDIT_THUMBNAIL") types.isUnapprovedCauseThumbnail = true;
              if (actionType === "EDIT_DESCRIPTION") types.isUnapprovedCauseDescription = true;
              if (actionType === "EDIT_NAME") types.isUnapprovedCauseName = true;
              if (actionType === "DELETE") types.isUnapprovedCausePackDeleting = true;
            });

            resolve({
              explain: _.uniqWith(explain, _.isEqual),
              types: types,
              pkgInspectData: {
                needThumbnailEditing: inspectData.get("needThumbnailEditing"),
                needNameEditing: inspectData.get("needNameEditing"),
                needDescriptionEditing: inspectData.get("needDescriptionEditing"),
                needPackageDeleting: inspectData.get("needPackageDeleting"),
                inspectId: inspectData.id,
              },
            });
          })
          .catch((err) => {
            console.log(err);
            reject("Failed to get unapprovedCause type of package.");
          }),
      )
      .catch((err) => {
        console.log(err);
        reject("Failed to get PackageInspectionData");
      });
  });
};

export const getPkgInfoAndInspectionDataById = (pkgId) => {
  return new Promise((resolve, reject) =>
    new Parse.Query(Parse.Object.extend("EmoticonPackage"))
      .get(pkgId)
      .then(async (pkg) =>
        resolve({
          name: pkg.get("name"),
          description: pkg.get("description"),
          status: pkg.get("status"),
          thumbnail: pkg.get("thumbnail"),
          unapprovedCause: await getPkgUnapprovedCauseData(pkg),
          textLang: pkg.get("textLang"),
          packType: pkg.get("packType"),
          movingStatus: pkg.get("movingStatus"),
          keyTexts: pkg.get("keyTexts") || [],
        }),
      )
      .catch((err) => {
        console.log(err);
        if (err.code === 101 && err.message === "Object not found.") {
          reject("Package does not exist.");
        } else {
          reject("Failed to load package infomation.");
        }
      }),
  );
};

export const getPkgStatus = (pkgId) => {
  return new Promise((resolve, reject) =>
    new Parse.Query(Parse.Object.extend("EmoticonPackage"))
      .get(pkgId)
      .then((pkg) => resolve(pkg.get("status")))
      .catch((err) => {
        console.log(err);
        err.message === "Object not found." && err.code === 101 ? reject("Package not found.") : reject("Failed to load package status.");
      }),
  );
};

export const getStickerList = (pkgId) => {
  return new Promise((resolve, reject) =>
    new Parse.Query(Parse.Object.extend("Emoticon"))
      .include("package")
      .exists("origin")
      .ascending("createdAt")
      .limit(1000000)
      .equalTo("package", new Parse.Object("EmoticonPackage", { id: pkgId }))
      .find()
      .then((stickers) => resolve(stickers))
      .catch((err) => {
        console.log(err);
        reject("Failed to load stickers.");
      }),
  );
};

//packageId로 PackageInspectionData 정보 가져오기
export const getPkgInspectionDataByPkg = (pkg) => {
  return new Parse.Query(Parse.Object.extend("PackageInspectionData")).equalTo("package", pkg).first();
};

//packageId로 해당 sticker list 와 해당 StickerInspectionData 가져오기
export const getStickersAndInspectDataByPkgId = (pkgId) => {
  const stickerQuery = new Parse.Query(Parse.Object.extend("Emoticon"))
    .include("package")
    .exists("origin")
    .ascending("createdAt")
    .limit(1000000)
    .equalTo("package", new Parse.Object("EmoticonPackage", { id: pkgId }));

  return new Parse.Query(Parse.Object.extend("StickerInspectionData"))
    .matchesQuery("sticker", stickerQuery)
    .include("sticker")
    .limit(1000000)
    .find();
};

export const getEmojiCategoryByIndexes = (emojiIndexes) =>
  new Promise((resolve, reject) =>
    new Parse.Query(Parse.Object.extend("EmojiCategory"))
      .equalTo("isTopRank", true)
      .containedIn("index", emojiIndexes)
      .find()
      .then((emojiCtgs) => resolve(emojiCtgs))
      .catch((err) => {
        console.log(err);
        reject("Failed to load emojiCategory data.");
      }),
  );

/**
 * isTopRank가 true인(297개) 이모지 카테고리 가져오기
 */
export const getIsOnEmojiCategories = () =>
  new Promise((resolve, reject) =>
    new Parse.Query(Parse.Object.extend("EmojiCategory"))
      .equalTo("isTopRank", true)
      .ascending("index")
      .limit(10000)
      .find()
      .then((emojiCtgs) => resolve(emojiCtgs))
      .catch((err) => {
        console.log(err);
        reject("Failed to load isOn emojiCategory data.");
      }),
  );

export const getPkgInfoAndStickerInfoById = (pkgId, page) =>
  new Promise((resolve, reject) =>
    page === "myPackage"
      ? getStickerList(pkgId)
          .then(async (stickers) => {
            const promises = stickers.map(async (sticker) => ({
              stickerId: sticker.id,
              stickerImg: sticker.get("origin"),
              language: sticker.get("textLang"),
              tagInfo: await getEmojiCategoryByIndexes(sticker.get("emojiCategories") || []),
            })); //스티커 필요 데이터만 반환

            await Promise.all(promises)
              .then((
                pStickers, //패키지 정보 구한 후 데이터 state에 추가
              ) => 
                getPkgInfoById(pkgId)
                  .then((pkgInfo) => resolve({ stickerList: pStickers, pkgInfo, isLoading: false, newKeyTexts: pkgInfo.keyTexts }))
                  .catch((getPkgInfoErr) => reject("Failed to load pack info.")),
              )
              .catch((getCtgErr) => reject("Failed to get tag info."));
          })
          .catch((getStickersErr) => reject("Failed to load sticker list."))
      : getStickersAndInspectDataByPkgId(pkgId)
          .then(async (stickerInspectData) => {
            const promises = stickerInspectData.map(async (inspectData) => ({
              stickerId: inspectData.get("sticker").id,
              stickerImg: inspectData.get("sticker").get("origin"),
              isPassed: inspectData.get("isPassed"),
              needTagEditing: inspectData.get("needTagEditing"),
              needImgEditing: inspectData.get("needImgEditing"),
              needStickerDeleting: inspectData.get("needStickerDeleting"),
              unapprovedCause: await inspectData
                .get("unapprovedCause")
                .query()
                .ascending("createdAt")
                .find(),
              language: inspectData.get("sticker").get("textLang"),
              tagInfo: await getEmojiCategoryByIndexes(inspectData.get("sticker").get("emojiCategories") || []),
            })); //스티커 필요 데이터만 반환

            await Promise.all(promises)
              .then((
                pStickers, //패키지 정보 구한 후 데이터 state에 추가
              ) =>
                getPkgInfoAndInspectionDataById(pkgId)
                  .then((pkgInfo) => resolve({ stickerList: pStickers, pkgInfo, isLoading: false }))
                  .catch((getPkgInfoErr) => reject("Failed to load package info.")),
              )
              .catch((getCtgErr) => reject("Failed to get tag info."));
          })
          .catch((getStickersErr) => reject("Failed to load sticker list.")),
  );

export const getMoneyBenefitOfUser = (user) => {
  const basedDateOfChart = new Date("2018-04"); //월별 수익 정산이 시작 된 날짜.
  let accRevenue = 0; //누적 수익
  let moneyBenefitData = new Map(); //월별 누적 수익
  let startedDate = user.createdAt; // 크리에이터가 회원 가입한 날짜. 수익 데이터를 보여줘야 하는 년도 기준을 위해 설정.

  if (basedDateOfChart > startedDate) {
    // 월별 수익 정산 전 크리에이터가 회원가입했을 경우 수익이 유효한 최초 년월을 월별 수익 정산이 시작된 날짜로 설정.
    startedDate = basedDateOfChart;
  }

  //유효한 최초 년도 1월부터 현재 달까지 월별 키-YYYYMM- 추가
  getArraysBetweenTwoDates(
    moment(startedDate)
      .startOf("year")
      .toDate(),
    new Date(),
    "month",
  ).map((date) => moneyBenefitData.set(moment(date).format("YYYYMM"), 0));

  return new Promise((resolve, reject) =>
    new Parse.Query(Parse.Object.extend("Money"))
      .equalTo("user", user)
      .greaterThan("money", 0)
      .ascending("earnedDate")
      .limit(Number.MAX_SAFE_INTEGER)
      .find()
      .then((moneyDatas) => {
        moneyDatas.forEach((money, idx) => {
          accRevenue = Number((accRevenue + money.get("money")).toFixed(2));

          // earnedDate or money의 생성 날짜로 월별 수익 그래프 키 생성
          const moneyDate = money.get("earnedDate") ? money.get("earnedDate") : moment(money.createdAt).format("YYYYMM");

          if (moneyBenefitData.has(moneyDate)) {
            moneyBenefitData.set(moneyDate, Number((moneyBenefitData.get(moneyDate) + money.get("money")).toFixed(2)));
          }
        });

        resolve({ accRevenue, moneyBenefitData });
      })
      .catch((err) => {
        console.log(err);
        reject(err);
      }),
  );
};

export const getValidLastMoneyDate = () => {
  // 정산이 완료된 마지막 날짜를 가져오는 함수
  return new Promise((resolve, reject) => {
    new Parse.Query(Parse.Object.extend("WebService"))
      .equalTo("url", "stickerfarm.revenueReport")
      .first()
      .then((data) => {
        const validLastMoneyDate = data.get("validLastMoneyDate");
        if (!validLastMoneyDate) {
          reject("Valid last money date is not exist.");
        }
        resolve(validLastMoneyDate);
      })
      .catch((err) => {
        console.log(err);
        reject("Failed to get data of valid last money date");
      });
  });
};

export const getReportData = async (user) => {
  try {
    const reportDataList = await new Parse.Query(Parse.Object.extend("CreatorReport"))
      .ascending("date")
      .equalTo("user", user)
      .limit(Number.MAX_SAFE_INTEGER)
      .find();

    if (reportDataList.length === 0) {
      //creatorReport가 없을 경우
      throw new Error("Report does not exist.");
    }

    let userReportData = {
      dateList: [],
      viewPoint: [],
      sharePoint: [],
      activityPoint: [],
      validLastReportData: {},
      validLastDate: "",
    };

    reportDataList.forEach((data, idx) => {
      userReportData.dateList.push(data.get("date"));
      userReportData.viewPoint.push(data.get("viewPoint"));
      userReportData.sharePoint.push(data.get("sharePoint"));
      userReportData.activityPoint.push(data.get("activityPoint"));

      if (idx === reportDataList.length - 1) {
        //마지막일 경우
        userReportData.validLastReportData = {
          viewPoint: data.get("viewPoint"),
          sharePoint: data.get("sharePoint"),
          activityPoint: data.get("activityPoint"),
        };
        userReportData.validLastDate = data.get("date");
      }
    });

    return userReportData;
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const getCreditOfUser = async (user) => {
  let credit = 0;
  try {
    const creditData = await new Parse.Query(Parse.Object.extend("Money"))
      .equalTo("user", user)
      .limit(Number.MAX_SAFE_INTEGER)
      .find();

    creditData.forEach((money) => (credit = Number((credit + money.get("money")).toFixed(2))));

    return credit;
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const getRemittanceDataOfUser = async (user) => {
  try {
    const payouts = new Parse.Query(Parse.Object.extend("Payout"))
      .descending("createdAt")
      .equalTo("user", user)
      .limit(Number.MAX_SAFE_INTEGER)
      .find();

    return payouts;
  } catch (err) {
    console.log(err);
    throw err;
  }
};
